import React, { useEffect, useState } from 'react';
import ApiService from '../ApiService/ApiService';
import './RateList.css';
import { FaUser, FaEdit, FaSave, FaTimes, FaPlus, FaSpinner } from 'react-icons/fa';
import { GiGoldBar, GiMetalBar } from 'react-icons/gi';

const RateList = () => {
    const currentDate = new Date().toISOString().split('T')[0];

    const [rates, setRates] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editableRate, setEditableRate] = useState(null);
    const [newRate, setNewRate] = useState({
        goldRate: '',
        silverRate: '',
        createdAt: currentDate,
        createdBy: 'admin',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const fetchRates = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchedRates = await ApiService.getRates();
            const ratesObject = fetchedRates.reduce((acc, rate) => {
                acc[rate.id] = rate;
                return acc;
            }, {});
            setRates(ratesObject);
        } catch (error) {
            console.error('Failed to fetch rates:', error);
            setError('Failed to fetch rates. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRates();
    }, []);

    const handleChange = (e, field) => {
        const value = e.target.value;
        setEditableRate({ ...editableRate, [field]: value });
    };

    const startEditing = (id) => {
        setEditingId(id);
        setEditableRate({ ...rates[id] });
        setError('');
        setSuccessMessage('');
    };

    const saveEdit = async () => {
        if (!editableRate) return;

        setIsLoading(true);
        setError(null);

        try {
            const rateData = {
                goldRate: editableRate.goldRate,
                silverRate: editableRate.silverRate,
                createdAt: new Date(editableRate.createdAt).toISOString(),
                createdBy: editableRate.createdBy,
            };

            const updatedRate = await ApiService.updateRate(editingId, rateData);
            setRates({ ...rates, [editingId]: updatedRate });
            setEditingId(null);
            setEditableRate(null);
            setSuccessMessage('Rate updated successfully!');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Failed to update rate:', error);
            setError('Failed to update rate. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditableRate(null);
        setError('');
    };

    const handleNewRateChange = (e, field) => {
        const value = e.target.value;
        setNewRate({ ...newRate, [field]: value });
        setError('');
    };

    const addNewRate = async () => {
        if (!newRate.goldRate || !newRate.silverRate) {
            setError('Gold and Silver rates are required!');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const rateData = {
                goldRate: newRate.goldRate,
                silverRate: newRate.silverRate,
                createdAt: new Date(newRate.createdAt).toISOString(),
                createdBy: 'admin',
            };

            await ApiService.addRate(rateData);

            // Reset form
            setNewRate({
                goldRate: '',
                silverRate: '',
                createdAt: currentDate,
                createdBy: 'admin',
            });

            // Refresh rates
            await fetchRates();

            setSuccessMessage('New rate added successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Failed to add rate:', error);
            setError('Failed to add rate. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="rate-container">
            <h4 className="rate-header">
                Rates for Gold and Silver
            </h4>

            {/* Status Messages */}
            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {/* Add New Rate Section */}
            <div className="add-rate-container">
                <div className="input-fields-group">
                    <div className="input-group">
                        <label>Gold Rate:</label>
                        <div className="input-with-icon">
                            <input
                                type="number"
                                value={newRate.goldRate}
                                onChange={(e) => handleNewRateChange(e, 'goldRate')}
                                className="rate-input"
                                placeholder="Enter gold rate"
                                min="0"
                                step="0.01"
                            />
                            <GiGoldBar className="input-icon gold-icon" />
                        </div>
                    </div>

                    <div className="input-group">
                        <label>Silver Rate:</label>
                        <div className="input-with-icon">
                            <input
                                type="number"
                                value={newRate.silverRate}
                                onChange={(e) => handleNewRateChange(e, 'silverRate')}
                                className="rate-input"
                                placeholder="Enter silver rate"
                                min="0"
                                step="0.01"
                            />
                            <GiMetalBar className="input-icon silver-icon" />
                        </div>
                    </div>

                    <div className="input-group">
                        <label>Created By:</label>
                        <div className="input-with-icon">
                            <input
                                type="text"
                                value="admin"
                                readOnly
                                className="rate-input"
                            />
                            <FaUser className="input-icon user-icon" />
                        </div>
                    </div>

                    <button
                        onClick={addNewRate}
                        className="add-rate-button"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <><FaSpinner className="button-icon spin" /> Adding...</>
                        ) : (
                            <><FaPlus className="button-icon" /> Add Rate</>
                        )}
                    </button>
                </div>
            </div>

            {/* Data Table */}
            {isLoading && Object.keys(rates).length === 0 ? (
                <div className="loading-container">
                    <FaSpinner className="spin" /> Loading rates...
                </div>
            ) : (
                <table className="rates-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Gold Rate</th>
                            <th>Silver Rate</th>
                            <th>Created At</th>
                            <th>Created By</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(rates).length === 0 ? (
                            <tr>
                                <td colSpan="6" className="no-data">No rates available</td>
                            </tr>
                        ) : (
                            Object.keys(rates).map((id) => {
                                const rate = rates[id];
                                return (
                                    <tr key={id} className="table-row">
                                        <td>{rate.id}</td>
                                        <td>
                                            {editingId === id ? (
                                                <div className="input-with-icon">
                                                    <input
                                                        type="number"
                                                        value={editableRate.goldRate}
                                                        onChange={(e) => handleChange(e, 'goldRate')}
                                                        className="table-input"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <GiGoldBar className="input-icon gold-icon" />
                                                </div>
                                            ) : rate.goldRate !== '0' ? (
                                                <div className="value-with-icon">
                                                    {rate.goldRate}
                                                    <GiGoldBar className="value-icon gold-icon" />
                                                </div>
                                            ) : (
                                                'N/A'
                                            )}
                                        </td>
                                        <td>
                                            {editingId === id ? (
                                                <div className="input-with-icon">
                                                    <input
                                                        type="number"
                                                        value={editableRate.silverRate}
                                                        onChange={(e) => handleChange(e, 'silverRate')}
                                                        className="table-input"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <GiMetalBar className="input-icon silver-icon" />
                                                </div>
                                            ) : rate.silverRate !== '0' ? (
                                                <div className="value-with-icon">
                                                    {rate.silverRate}
                                                    <GiMetalBar className="value-icon silver-icon" />
                                                </div>
                                            ) : (
                                                'N/A'
                                            )}
                                        </td>
                                        <td>
                                            {editingId === id ? (
                                                <input
                                                    type="date"
                                                    value={editableRate.createdAt.split('T')[0]}
                                                    onChange={(e) => handleChange(e, 'createdAt')}
                                                    className="table-input"
                                                />
                                            ) : (
                                                rate.createdAt.split('T')[0]
                                            )}
                                        </td>
                                        <td>
                                            {editingId === id ? (
                                                <div className="input-with-icon">
                                                    <input
                                                        type="text"
                                                        value={editableRate.createdBy}
                                                        onChange={(e) => handleChange(e, 'createdBy')}
                                                        className="table-input"
                                                    />
                                                    <FaUser className="input-icon user-icon" />
                                                </div>
                                            ) : (
                                                <div className="value-with-icon">
                                                    {rate.createdBy}
                                                    <FaUser className="value-icon user-icon" />
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {editingId === id ? (
                                                <div className="action-buttons">
                                                    <button
                                                        onClick={saveEdit}
                                                        className="save-button"
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? (
                                                            <><FaSpinner className="button-icon spin" /> Saving...</>
                                                        ) : (
                                                            <><FaSave className="button-icon" /> Save</>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="cancel-button"
                                                        disabled={isLoading}
                                                    >
                                                        <FaTimes className="button-icon" /> Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => startEditing(id)}
                                                    className="edit-button"
                                                    disabled={isLoading}
                                                >
                                                    <FaEdit className="button-icon" /> Edit
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default RateList;